import React, { useEffect, useRef, useState } from "react";
import { Eraser, X, Undo2, Trash2, Plus, Minus, ChevronLeft } from "lucide-react";
import { EraseMark } from "../lib/sam2Erase";
import { useDebajoDeMd } from "../hooks/useEsMobile";

interface Props {
  frameUrl: string; // dataURL del frame que se muestra/marca
  frameWidth: number; // ancho real del video (px)
  frameHeight: number;
  // El padre (VideoEditor) sube el frame a Supabase, firma y llama a SAM.
  // Devuelve un dataURL/URL de la máscara para overlay, o lanza.
  onRequestMask: (marks: EraseMark[]) => Promise<string>;
  onConfirm: (marks: EraseMark[]) => void; // dispara el modal de confirmación en el padre
  onClose: () => void;
  /**
   * Volver a la pantalla de la que se vino (el panel del clip). Opcional: sin
   * esto no se dibuja la flecha. Hace lo mismo que cerrar —el padre devuelve el
   * panel previo— pero dicho como "atrás", que es lo que el usuario está
   * haciendo cuando entró desde Herramientas IA.
   */
  onBack?: () => void;
}

// Pincel inteligente por CLICKS en una MODAL grande (para ver el frame grande).
// El usuario toca DENTRO del objeto (1+ taps) y SAM lo "snapea". NO se traza el
// borde — cada tap es un punto. Tras cada tap se pide el preview automáticamente
// (con guard de carrera). +/- para incluir/excluir. Diseño Atlas.
const EraseBrushPanel: React.FC<Props> = ({
  frameUrl,
  frameWidth,
  frameHeight,
  onRequestMask,
  onConfirm,
  onClose,
  onBack,
}) => {
  // En el teléfono esto es una HOJA que sube desde abajo, como el resto de los
  // paneles del editor; en escritorio sigue siendo la modal centrada, que es
  // donde el frame se ve grande. Ver
  // ADR-2026-09-18-borrar-objeto-es-una-hoja-en-el-telefono.
  const enElTelefono = useDebajoDeMd();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  // Overlay verde ya compuesto a partir de la máscara (canvas offscreen).
  const maskOverlayRef = useRef<HTMLCanvasElement | null>(null);
  const reqIdRef = useRef(0); // para descartar previews viejos (carrera)
  const [marks, setMarks] = useState<EraseMark[]>([]);
  const [mode, setMode] = useState<"include" | "exclude">("include");
  const [maskUrl, setMaskUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Render: frame + overlay verde de máscara + puntos.
  const redraw = () => {
    const cv = canvasRef.current,
      img = imgRef.current;
    if (!cv || !img) return;
    const ctx = cv.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, cv.width, cv.height);
    ctx.drawImage(img, 0, 0, cv.width, cv.height);
    if (maskOverlayRef.current) {
      ctx.globalAlpha = 0.55;
      ctx.drawImage(maskOverlayRef.current, 0, 0, cv.width, cv.height);
      ctx.globalAlpha = 1;
    }
    const sx = cv.width / frameWidth,
      sy = cv.height / frameHeight;
    for (const m of marks) {
      const p = m.points[0];
      ctx.fillStyle = m.mode === "include" ? "#22c55e" : "#ef4444";
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(p.x * sx, p.y * sy, 7, 0, Math.PI * 2);
      ctx.fill();
      ctx.stroke();
    }
  };

  // Compone un overlay VERDE a partir de la máscara. La máscara puede venir como
  // binaria (objeto blanco sobre negro) o como recorte sobre blanco/transparente;
  // pintamos de verde los píxeles "del objeto" (no-negros y no-transparentes).
  const buildGreenOverlay = (im: HTMLImageElement) => {
    const w = im.naturalWidth || frameWidth;
    const h = im.naturalHeight || frameHeight;
    const off = document.createElement("canvas");
    off.width = w;
    off.height = h;
    const octx = off.getContext("2d");
    if (!octx) return null;
    octx.drawImage(im, 0, 0, w, h);
    let data: ImageData;
    try {
      data = octx.getImageData(0, 0, w, h);
    } catch {
      // canvas tainted (máscara cross-origin sin CORS): no podemos tintar,
      // devolvemos la imagen tal cual.
      return off;
    }
    const px = data.data;
    for (let i = 0; i < px.length; i += 4) {
      const r = px[i],
        g = px[i + 1],
        b = px[i + 2],
        a = px[i + 3];
      // "Pertenece al objeto" = visible y con algo de brillo (no negro puro).
      const lum = (r + g + b) / 3;
      const isObject = a > 20 && lum > 24;
      if (isObject) {
        px[i] = 34; // verde Atlas (#22c55e aprox)
        px[i + 1] = 197;
        px[i + 2] = 94;
        px[i + 3] = 255;
      } else {
        px[i + 3] = 0; // transparente fuera del objeto
      }
    }
    octx.putImageData(data, 0, 0);
    return off;
  };

  // Cargar el overlay de máscara cuando cambia.
  useEffect(() => {
    if (!maskUrl) {
      maskOverlayRef.current = null;
      redraw();
      return;
    }
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      maskOverlayRef.current = buildGreenOverlay(im);
      redraw();
    };
    im.src = maskUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maskUrl]);

  // Cargar el frame.
  useEffect(() => {
    const im = new Image();
    im.crossOrigin = "anonymous";
    im.onload = () => {
      imgRef.current = im;
      redraw();
    };
    im.onerror = () => setError("No se pudo cargar el frame del clip.");
    im.src = frameUrl;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frameUrl]);

  useEffect(redraw, [marks, frameWidth, frameHeight]); // eslint-disable-line

  // Cerrar con Escape.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Coords del canvas → coords del frame (px reales).
  const toFrame = (e: React.PointerEvent) => {
    const cv = canvasRef.current!;
    const r = cv.getBoundingClientRect();
    const cx = (e.clientX - r.left) * (cv.width / r.width);
    const cy = (e.clientY - r.top) * (cv.height / r.height);
    return { x: (cx / cv.width) * frameWidth, y: (cy / cv.height) * frameHeight };
  };

  // Pide el preview a SAM con las marcas dadas (descarta resultados viejos).
  const preview = async (allMarks: EraseMark[]) => {
    if (!allMarks.length) {
      setMaskUrl(null);
      return;
    }
    const id = ++reqIdRef.current;
    setError(null);
    setLoading(true);
    try {
      const url = await onRequestMask(allMarks);
      if (id === reqIdRef.current) setMaskUrl(url);
    } catch (e: any) {
      if (id === reqIdRef.current)
        setError(e?.message || "No se pudo calcular la selección.");
    } finally {
      if (id === reqIdRef.current) setLoading(false);
    }
  };

  // Un click = un punto adentro del objeto. Auto-preview.
  const onCanvasClick = (e: React.PointerEvent) => {
    const p = toFrame(e);
    const next: EraseMark[] = [...marks, { type: "point", mode, points: [p] }];
    setMarks(next);
    void preview(next);
  };

  const undoLast = () => {
    const next = marks.slice(0, -1);
    setMarks(next);
    void preview(next);
  };

  const clearAll = () => {
    reqIdRef.current++;
    setMarks([]);
    setMaskUrl(null);
    setError(null);
  };

  return (
    <div
      /* `data-clip-ui`: el editor tiene un listener de `pointerdown` en
         `document` que SUELTA el clip seleccionado ante cualquier click que no
         caiga en un clip o en UI del clip. Sin esta marca, el primer toque acá
         adentro —el lienzo, un botón de la barra, la flecha de volver— dejaba
         al editor sin selección, y al volver el panel del clip aparecía vacío
         ("Editar clip" sin nada adentro).
         Ver ADR-2026-09-18-el-pincel-no-suelta-el-clip. */
      data-clip-ui="true"
      /* `bg-black/60` y no `bg-brand-black/60`: los tokens `brand-*` no
         llevan `<alpha-value>`, así que con el modificador de opacidad
         Tailwind NO emite regla — el velo era transparente y el panel flotaba
         sin separarse de lo de atrás. Lo mismo valía para el escenario del
         lienzo y los carteles de abajo.
         Ver ADR-2026-09-17-borrar-objeto-entra-en-el-telefono. */
      className={`fixed inset-0 z-[9999] flex justify-center bg-black/60 backdrop-blur-sm ${
        enElTelefono ? "items-end" : "items-center p-4"
      }`}
      onPointerDown={(e) => {
        // Click fuera de la card cierra.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`flex w-full flex-col overflow-hidden bg-surface-card ${
          enElTelefono
            ? /* Hoja: pegada abajo, a todo el ancho, con las esquinas de
                 arriba redondeadas y la subida de siempre. El tope es 92dvh
                 —`dvh` y no `vh` porque la barra del navegador aparece y
                 desaparece— y el pie reserva el safe area. */
              "max-h-[92dvh] rounded-t-2xl shadow-[0_-4px_20px_rgba(2,6,43,0.18)] animate-in slide-in-from-bottom duration-300"
            : "max-w-4xl max-h-[92vh] rounded-lg shadow-md"
        }`}
        style={
          enElTelefono
            ? { paddingBottom: "env(safe-area-inset-bottom, 0px)" }
            : undefined
        }
      >
        {/* Manija — el mismo hilo gris que el resto de las hojas del editor:
        es lo que dice "esto sube desde abajo y se puede bajar". */}
        {enElTelefono && (
          <div aria-hidden="true" className="flex justify-center pt-2.5 pb-1">
            <span className="h-1 w-9 rounded-full bg-black/[0.18]" />
          </div>
        )}
        {/* Header */}
        <div className="flex items-center gap-2 border-b border-neutral-200 px-5 py-3">
          {/* Volver — a la izquierda del título, antes del ícono: se entra acá
          desde Herramientas IA del clip, así que hay una pantalla anterior
          concreta a la que volver. Mismo botón que las sub-pantallas de la
          hoja (Eliminar silencios, Cambiar voz, Video con IA al extender). */}
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="-ml-1.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-brand-black/50 hover:bg-black/[0.06] hover:text-brand-black/80 transition-colors"
              title="Volver"
              aria-label="Volver"
            >
              <ChevronLeft size={18} />
            </button>
          )}
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-primary/10 text-brand-primary">
            <Eraser size={17} />
          </span>
          <div className="min-w-0">
            <h2 className="text-[15px] font-semibold text-brand-black/90 leading-tight">
              Borrar objeto
            </h2>
            <p className="text-[12px] text-neutral-500 leading-tight">
              Tocá <strong>dentro</strong> del objeto a borrar (no lo bordees).
            </p>
          </div>
          <button
            onClick={onClose}
            className="ml-auto flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 hover:bg-neutral-100 hover:text-brand-black/90"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        {/* Barra. En el teléfono se parte en DOS filas (`flex-wrap`): el par
        Borrar esto / Conservar en la primera y Deshacer / Limpiar en la
        segunda, alineados a la izquierda. En una sola fila no entraban y
        "Limpiar" quedaba cortado contra el borde. El `ml-auto` que manda
        Deshacer a la derecha vuelve recién en `sm`. */}
        <div className="flex flex-wrap items-center gap-2 px-5 py-2.5 border-b border-neutral-200">
          {/* El radio de la pastilla activa es el del contenedor MENOS su
          padding (8 − 2 = 6): con los dos en el mismo escalón, el relleno azul
          se veía cuadrado adentro de una caja curva. */}
          <div className="flex rounded-lg border border-neutral-200 p-0.5">
            <button
              onClick={() => setMode("include")}
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] font-medium transition ${
                mode === "include"
                  ? "bg-brand-primary text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              <Plus size={13} /> Borrar esto
            </button>
            <button
              onClick={() => setMode("exclude")}
              className={`flex items-center gap-1 whitespace-nowrap rounded-md px-2.5 py-1 text-[12px] font-medium transition ${
                mode === "exclude"
                  ? "bg-red-600 text-white"
                  : "text-neutral-600 hover:bg-neutral-100"
              }`}
            >
              <Minus size={13} /> Conservar
            </button>
          </div>
          {/* Deshacer y Limpiar van JUNTOS. `w-full` en el teléfono los baja
          a su propia fila —un item que ocupa todo el ancho no puede compartir
          renglón con el par de modos—, así no queda Deshacer arriba y Limpiar
          solo abajo. En `sm` vuelven a la derecha de la misma fila. */}
          <div className="flex w-full items-center gap-2 sm:ml-auto sm:w-auto">
            <button
              onClick={undoLast}
              disabled={!marks.length}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-neutral-200 px-2.5 py-1.5 text-[12px] text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
            >
              <Undo2 size={13} /> Deshacer
            </button>
            <button
              onClick={clearAll}
              disabled={!marks.length}
              className="flex items-center gap-1 whitespace-nowrap rounded-md border border-neutral-200 px-2.5 py-1.5 text-[12px] text-neutral-600 hover:bg-neutral-100 disabled:opacity-40"
            >
              <Trash2 size={13} /> Limpiar
            </button>
          </div>
        </div>

        {/* Lienzo grande */}
        <div className="flex flex-1 items-center justify-center overflow-auto bg-black/90 p-4">
          <div className="relative inline-block">
            <canvas
              ref={canvasRef}
              width={frameWidth}
              height={frameHeight}
              className={`w-auto max-w-full rounded-md touch-none cursor-crosshair shadow-md ${
                // En la hoja, entre la cabecera, la barra de dos filas y el
                // pie quedan ~240px de cromo: con `60vh` el lienzo empujaba
                // los botones fuera de la pantalla.
                enElTelefono ? "max-h-[52dvh]" : "max-h-[60vh]"
              }`}
              onPointerDown={onCanvasClick}
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center rounded-md bg-black/30">
                <span className="rounded-md bg-black/70 px-3 py-1.5 text-[13px] text-white">
                  Calculando selección…
                </span>
              </div>
            )}
            {!marks.length && !loading && (
              <div className="pointer-events-none absolute inset-x-0 bottom-3 flex justify-center">
                <span className="rounded-full bg-black/70 px-3 py-1 text-[12px] text-white">
                  Tocá el objeto para seleccionarlo
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer. En el teléfono, en dos filas: los botones ARRIBA y la
        ayuda DEBAJO, en una sola línea. Antes la ayuda iba al costado con
        `flex-1` y se partía en cuatro renglones que empujaban los botones
        contra el borde. En `sm` vuelve al reparto de siempre (texto a la
        izquierda, botones a la derecha) con los `order`. */}
        <div className="flex flex-col gap-2 border-t border-neutral-200 px-5 py-3 sm:flex-row sm:items-center sm:gap-3">
          <div className="flex items-center gap-2 sm:order-2 sm:ml-auto">
            <button
              onClick={onClose}
              className="h-9 flex-1 rounded-md border border-neutral-200 px-4 text-[13px] text-neutral-600 hover:bg-neutral-100 sm:flex-none"
            >
              Cancelar
            </button>
            <button
              onClick={() => onConfirm(marks)}
              disabled={!maskUrl || loading}
              className="h-9 flex-1 whitespace-nowrap rounded-md bg-brand-primary px-5 text-[13px] font-medium text-white hover:bg-brand-primary-hover disabled:opacity-40 sm:flex-none"
            >
              Borrar objeto
            </button>
          </div>
          {/* Una sola línea: el texto se acortó para que entre sin recortarse
             en un teléfono angosto (decía "…para sacar lo que tomó de más").
             `truncate` es el último recurso, no el plan. */}
          {error ? (
            <p className="truncate text-[12px] text-red-600 sm:order-1 sm:flex-1">
              {error}
            </p>
          ) : (
            <p className="truncate text-[11.5px] text-neutral-500 sm:order-1 sm:flex-1 sm:text-[12px]">
              Sumá toques si agarró poco, o usá <strong>−</strong> para quitar.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default EraseBrushPanel;
